(() => {
  if (customElements.get('size-catalog')) return;
  class SizeCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      const eventOptions = {signal: this.events.signal};
      this.form = this.querySelector('[data-size-filters]');
      this.size = this.querySelector('[data-size-select]');
      this.binder = this.querySelector('[data-binder-select]');
      this.grid = this.querySelector('[data-size-grid]');
      this.status = this.querySelector('[data-size-status]');
      this.empty = this.querySelector('[data-size-empty]');
      this.native = (this.dataset.nativeIds || '').split(',').filter(Boolean).map(id => document.getElementById(`shopify-section-${this.dataset.templateId}__${id}`)).filter(Boolean).map(element => ({element, display: element.style.display}));
      this.ready = false;
      this.filterToggle = this.querySelector('[data-size-filter-toggle]');
      this.mobile = matchMedia('(max-width: 700px)');
      this.filterOpen = false;
      this.filterToggle?.addEventListener('click', () => {
        this.filterOpen = !this.filterOpen;
        this.showControls();
      });
      this.mobile.addEventListener('change', () => this.showControls(), eventOptions);
      this.readURL();
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-size-select], [data-binder-select]')) this.refresh();
      });
      this.form.addEventListener('reset', event => {
        if (!this.querySelector('collection-filter-toolbar')) { event.preventDefault(); this.clear(); }
      });
      this.querySelector('[data-size-clear]').addEventListener('click', () => this.clear());
      window.addEventListener('popstate', () => { if (this.isConnected && !this.querySelector('collection-filter-toolbar')) { this.readURL(); this.refresh(false); } }, eventOptions);
      // custom.js rewrites quick-add IDs at document level; apply the size constraint last.
      window.addEventListener('change', event => {
        if (this.isConnected && this.contains(event.target)) this.onCardOptionChange(event);
      }, eventOptions);
      this.addEventListener('submit', event => {
        const item = this.items?.find(item => item.element.contains(event.target));
        if (!item || !this.hasSizeConstraint() || !event.target.matches('form[action*="/cart/add"]')) return;
        this.syncVariant(item);
        if (!item.purchasable) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      this.addEventListener('click', event => {
        const anchor = event.target.closest('[data-size-item] a');
        const item = anchor?.closest('[data-size-item]');
        if (!item?.dataset.matchingVariant || !this.hasSizeConstraint()) return;
        const url = new URL(anchor.href, location.origin);
        if (url.pathname === new URL(item.dataset.productUrl, location.origin).pathname) {
          url.searchParams.set('variant', item.dataset.matchingVariant);
          anchor.href = url.href;
        }
      }, true);
      this.refresh(false);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.native?.forEach(({element, display}) => { element.style.display = display; });
    }
    showControls() {
      const toolbar = this.querySelector('collection-filter-toolbar');
      if (toolbar) { toolbar.updateCount?.(); return; }
      // Older cached markup remains usable while the new toggle rolls out.
      if (!this.filterToggle) { this.form.hidden = false; return; }
      this.form.hidden = this.mobile.matches && !this.filterOpen;
      this.filterToggle.hidden = !this.mobile.matches;
      this.filterToggle.setAttribute('aria-expanded', String(!this.form.hidden));
      const count = Number(Boolean(this.size.value)) + Number(Boolean(this.binder.value));
      this.filterToggle.textContent = `${this.filterToggle.dataset.label}${count ? ` (${count})` : ''}`;
    }
    hasSizeConstraint() { return Boolean(this.size.value || this.binder.value); }
    active() {
      const params = new URL(location.href).searchParams;
      return this.hasSizeConstraint() || [...params].some(([key, value]) => value && (key.startsWith('filter.') || key === 'sort_by'));
    }
    requestContext() {
      const url = new URL(location.href);
      ['size', 'binder', 'page', 'section_id', 'sections'].forEach(name => url.searchParams.delete(name));
      url.searchParams.sort();
      url.hash = '';
      return url.href;
    }
    showNative(show) {
      // Native facet/sort updates may replace section wrappers without replacing this toolbar.
      this.native = (this.dataset.nativeIds || '').split(',').filter(Boolean)
        .map(id => document.getElementById(`shopify-section-${this.dataset.templateId}__${id}`)).filter(Boolean)
        .map(element => this.native.find(previous => previous.element === element) || {element, display: element.style.display});
      this.native.forEach(({element, display}) => { element.style.display = show ? display : 'none'; });
    }
    async populate() {
      const context = this.requestContext();
      const first = new URL(location.href);
      first.searchParams.delete('page');
      first.searchParams.delete('sections');
      first.searchParams.set('section_id', 'size-catalog-results');
      let next = first.href;
      const seen = new Set();
      const products = new Set();
      const fragment = document.createDocumentFragment();
      let scanned = 0;
      let total = null;
      while (next) {
        const url = new URL(next, location.origin);
        const page = url.searchParams.get('page');
        // Pagination links may omit locale/facet/sort context; retain the initial request.
        url.pathname = first.pathname;
        url.search = first.search;
        if (page) url.searchParams.set('page', page);
        url.searchParams.set('section_id', 'size-catalog-results');
        if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid pagination');
        seen.add(url.href);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        let markup;
        try {
          const response = await fetch(url, {signal: controller.signal});
          if (!response.ok) throw new Error('Could not load the full collection');
          markup = await response.text();
        } finally { clearTimeout(timer); }
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const section = doc.querySelector('[data-size-results]');
        if (!section) throw new Error('Missing collection page');
        scanned += Number(section.dataset.scanned);
        const pageTotal = Number(section.dataset.total);
        if (total !== null && total !== pageTotal) throw new Error('Collection changed during pagination');
        total = pageTotal;
        section.querySelectorAll('[data-size-item]').forEach(item => {
          if (!products.has(item.dataset.productId)) {
            products.add(item.dataset.productId);
            fragment.append(document.importNode(item, true));
          }
        });
        next = section.dataset.nextUrl;
      }
      if (!Number.isFinite(scanned) || !Number.isFinite(total) || scanned < total) throw new Error('Incomplete collection');
      this.grid.replaceChildren(fragment);
      this.items = [...this.grid.querySelectorAll('[data-size-item]')].map(element => ({
        element, choices: JSON.parse(element.dataset.sizeChoices || 'null') || [],
        variants: JSON.parse(element.querySelector('[data-size-variants]').textContent),
        optionNames: JSON.parse(element.dataset.optionNames || '[]'),
        optionValues: JSON.parse(element.dataset.optionValues || '[]')
      }));
      this.items.forEach(item => {
        const initial = item.variants.find(variant => String(variant.id) === item.element.dataset.initialVariant) || item.variants.find(variant => variant.available) || item.variants[0];
        item.selection = [...(initial?.options || [])];
        item.element.querySelectorAll('input[type="radio"]:checked').forEach(control => {
          const position = this.optionPosition(control);
          if (position >= 0) item.selection[position] = this.optionValue(item, control, position);
        });
      });
      this.loadedContext = context;
      this.ready = true;
    }
    readURL() {
      this.filterOpen = false;
      const params = new URL(location.href).searchParams;
      [this.size, this.binder].forEach(select => {
        const value = params.get(select.name) || '';
        select.value = [...select.options].some(option => option.value === value) ? value : '';
      });
    }
    writeURL() {
      const url = new URL(location.href);
      [this.size, this.binder].forEach(select => { if (select.value) url.searchParams.set(select.name, select.value); else url.searchParams.delete(select.name); });
      // The original page/facet/sort context remains intact for Clear and Back.
      history.replaceState(history.state, '', url);
    }
    clear() {
      const toolbar = this.querySelector('collection-filter-toolbar');
      if (toolbar) { toolbar.clear(); return; }
      this.size.value = ''; this.binder.value = ''; this.refresh();
    }
    async refresh(write = true) {
      if (!this.isConnected) return;
      this.dataset.filtering = String(this.active());
      this.showControls();
      if (write) this.writeURL();
      if (!this.active()) {
        this.showNative(true);
        this.grid.hidden = true;
        this.status.hidden = true;
        this.empty.hidden = true;
        return;
      }
      this.status.hidden = false;
      if (this.ready && this.loadedContext !== this.requestContext()) {
        this.ready = false;
        this.grid.hidden = true;
        this.showNative(true);
      }
      if (!this.ready) {
        this.status.textContent = this.dataset.loadingLabel || 'Loading matching sizes…';
        if (!this.loading) this.loading = this.populate().finally(() => { this.loading = null; });
        try { await this.loading; } catch {
          if (!this.isConnected) return;
          this.showNative(true);
          this.grid.hidden = true;
          if (this.active()) this.status.textContent = this.dataset.errorLabel || 'Size filters could not load. Browse below or try another size to retry.';
          return;
        }
      }
      if (!this.isConnected || !this.active()) return;
      if (this.loadedContext !== this.requestContext()) {
        this.ready = false;
        return this.refresh(false);
      }
      this.filter();
    }
    matchingChoices(item) {
      return item.choices.filter(choice => (!this.size.value || choice.family === this.size.value) && (!this.binder.value || choice.binder === this.binder.value));
    }
    choicePosition(choice, optionNames) {
      return Number.isInteger(choice.option_index) && choice.option_index >= 0 && choice.option_index < optionNames.length ? choice.option_index : optionNames.indexOf(choice.option_name);
    }
    variantMatches(variant, choices, optionNames, optionValues = []) {
      return choices.some(choice => {
        if (!choice.option_value) return true;
        const position = this.choicePosition(choice, optionNames);
        // Size belongs to its named option, never to an identically named colour.
        const translatedValue = Number.isInteger(choice.option_value_index) && choice.option_value_index >= 0 ? optionValues[position]?.[choice.option_value_index] : undefined;
        return position >= 0 && variant.options[position] === (translatedValue ?? choice.option_value);
      });
    }
    optionPosition(control) {
      const value = Number(control.dataset.optionPosition || control.closest('[data-option-position]')?.dataset.optionPosition);
      return Number.isInteger(value) && value > 0 ? value - 1 : -1;
    }
    optionValue(item, control, position = this.optionPosition(control)) {
      // Shopify radios can submit option-value IDs while variant.options contains names.
      const variant = item.variants.find(variant => String(variant.id) === control.dataset.variantId);
      if (variant?.options[position] !== undefined) return variant.options[position];
      const values = new Set(item.variants.map(variant => variant.options[position]));
      if (values.has(control.value)) return control.value;
      // Some valid colours have no variant at the product's initial size. Their labels
      // still contain the exact option name even when Shopify omits data-variant-id.
      const label = [...(control.labels || [])].map(label => (label.querySelector('.sr-only') || label).textContent.trim()).find(value => values.has(value));
      return label ?? control.value;
    }
    onCardOptionChange(event) {
      const control = event.target;
      if (!control.matches('input[type="radio"]') || !this.hasSizeConstraint()) return;
      const item = this.items?.find(item => item.element.contains(control));
      if (!item) return;
      const position = this.optionPosition(control);
      if (!Number.isInteger(position) || position < 0 || position >= item.optionNames.length) return;
      const constrained = new Set(this.matchingChoices(item).map(choice => this.choicePosition(choice, item.optionNames)));
      if (!constrained.has(position)) {
        item.selection[position] = this.optionValue(item, control, position);
      }
      // Native ProductCard updates its image and links before this bubbled handler.
      this.syncVariant(item);
    }
    syncVariant(item) {
      const {element, variants, optionNames, optionValues} = item;
      const matching = this.matchingChoices(item);
      const candidates = variants.filter(variant => this.variantMatches(variant, matching, optionNames, optionValues));
      const constrained = new Set(matching.map(choice => this.choicePosition(choice, optionNames)).filter(position => position >= 0));
      const preservesSelection = variant => variant.options.every((value, position) => constrained.has(position) || !item.selection[position] || item.selection[position] === value);
      const sameOptions = candidates.filter(preservesSelection);
      // Preserve the pictured/selected colour, including when that combination is sold out.
      // A missing combination requires an option change; never silently buy another colour.
      const variant = sameOptions.find(variant => variant.available) || sameOptions[0];
      const linkVariant = variant || candidates.find(variant => variant.available) || candidates[0];
      item.purchasable = Boolean(variant?.available);
      const matchingLink = element.querySelector('[data-size-matching-link]');
      matchingLink.hidden = !linkVariant;
      delete element.dataset.matchingVariant;
      if (linkVariant) {
        element.dataset.matchingVariant = linkVariant.id;
        const url = new URL(element.dataset.productUrl, location.origin);
        url.searchParams.set('variant', linkVariant.id);
        matchingLink.href = url.href;
        matchingLink.textContent = this.size.value ? (this.dataset.matchingSizeLabel || 'View {size} options').replace('{size}', this.size.selectedOptions[0].textContent) : (this.dataset.matchingBindingLabel || 'View matching binding');
        element.querySelectorAll('a[href]').forEach(anchor => {
          const destination = new URL(anchor.href, location.origin);
          if (destination.origin === url.origin && destination.pathname === url.pathname) {
            destination.searchParams.set('variant', linkVariant.id);
            anchor.href = destination.href;
          }
        });
      }
      if (variant) {
        element.querySelector('collection-offer')?.update?.(String(variant.id));
        item.selection = [...variant.options];
        element.querySelectorAll('input[type="radio"]').forEach(control => {
          const position = this.optionPosition(control);
          if (position >= 0) control.checked = this.optionValue(item, control, position) === variant.options[position];
        });
      }
      element.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        const idInput = form.querySelector('[name="id"]');
        if (idInput) {
          idInput.value = variant ? String(variant.id) : '';
          idInput.disabled = !item.purchasable;
        }
        form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => {
          button.disabled = !item.purchasable;
          button.setAttribute('aria-disabled', String(!item.purchasable));
        });
      });
      // Wishlist metadata must also refer to the visible size/colour combination.
      element.querySelectorAll('.wishlist-card-heart').forEach(button => {
        button.disabled = !variant;
        if (variant) {
          button.dataset.variantId = variant.id;
          button.dataset.variantTitle = variant.options.join(' / ');
          if (variant.image) button.dataset.variantImage = variant.image;
        }
      });
      let notice = element.querySelector('[data-size-unavailable]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.sizeUnavailable = '';
        notice.setAttribute('role', 'status');
        element.querySelector('.size-catalog__specs').append(notice);
      }
      notice.hidden = item.purchasable;
      notice.textContent = this.dataset.sizeUnavailable || (!variant ? 'This size and colour combination is unavailable. View matching options.' : 'This size and colour is sold out. Choose another option.');
    }
    filter() {
      let count = 0;
      this.items.forEach(item => {
        const matching = this.matchingChoices(item);
        item.element.hidden = this.hasSizeConstraint() && !matching.length;
        if (!item.element.hidden) {
          count++;
          if (this.hasSizeConstraint()) this.syncVariant(item);
        }
      });
      this.status.textContent = `${count} ${count === 1 ? (this.dataset.productOne || 'product') : (this.dataset.productMany || 'products')}`;
      this.empty.hidden = count !== 0;
      this.grid.hidden = false;
      this.showNative(false);
    }
  }
  customElements.define('size-catalog', SizeCatalog);
})();
